import { Component, type SyntheticEvent } from "react";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton, type IColumn, Separator } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import {
    SortDirection,
    type User,
    type UserGroupCreate,
    UserSortKey,
    type VehicleGroup,
} from "../../api";
import { doubleBindString } from "../../utils/double-bind";
import { createUuid, type UUID } from "../../utils/uuid";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { MultiSelectWithList } from "../organisms/multi-select-with-list";
import { ModalConfirmation } from "../atoms/modal-confirmation";
import { ElofleetDialog } from "../atoms/elofleet-dialog";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { defaultPageSize } from "../../utils/constants";

export interface FormCreateUserGroupProps {
    readonly onDialogCancel: () => void;
    readonly asDialogContent?: boolean;
    readonly isOpen: boolean;
}

/**
 * This form is part of the forms that're opened when clicking on the dropdown button in the
 * UserGroup list view.
 *
 * It is used to create a new UserGroup, but also allows to create assignments for Users and
 * VehicleGroups at the same time.
 */
import { store } from "../../store";

@observer
export class FormCreateUserGroup extends Component<FormCreateUserGroupProps> {
    @observable private accessor cancelConfirmationModalVisible = false;
    @observable private accessor vehicleGroupListVisible = false;

    @observable public accessor label = "";

    // These lists contain the entities that've been added via the multi-select dropdown.
    // I.e. the Users and VehicleGroups that have been selected for assignments.
    //
    // Once the user sends this form, these entities will be used to create the
    // actual new assignments.
    @observable private accessor addedUsers: UUID[] = [];
    @observable private accessor addedVehicleGroups: UUID[] = [];

    // The list state ids for the respective lists of added groups.
    private userListStateId = createUuid();
    private vehicleGroupListStateId = createUuid();

    private validationId = createUuid();

    constructor(props: FormCreateUserGroupProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryUserGroups.validation.create.updateModel(
            this.validationId,
            this.userGroupCreate,
        );
    }

    componentDidMount(): void {
        store.serviceListStates.users.initializeList(this.userListStateId, {
            query: () => {
                return {
                    pageSize: defaultPageSize,
                };
            },
            ignoreUrl: true,
        });
        store.serviceListStates.vehicleGroups.initializeList(this.vehicleGroupListStateId, {
            query: () => {
                return {
                    pageSize: defaultPageSize,
                };
            },
            ignoreUrl: true,
        });

        store.repositoryUserGroups.validation.create.initializeModel(
            this.validationId,
            this.userGroupCreate,
        );
    }

    @computed private get userGroupCreate(): UserGroupCreate {
        const create = {
            label: this.label,
            userIds: this.addedUsers,
            vehicleGroupIds: [],
        } as UserGroupCreate;

        // Only send any vehicleGroupIds, if the list is actually visible.
        if (this.vehicleGroupListVisible) {
            create.vehicleGroupIds = this.addedVehicleGroups;
        }

        return create;
    }

    /**
     * This is run if the user clicks the form's save button.
     */
    @action.bound private async createUserGroup(
        evt: SyntheticEvent<HTMLFormElement>,
    ): Promise<void> {
        evt.preventDefault();
        await store.repositoryUserGroups.create(this.userGroupCreate);
        this.reset();
        this.triggerValidation();

        this.closeForm();
    }

    @action.bound private reset(): void {
        this.label = "";
        this.addedUsers = [];
        this.addedVehicleGroups = [];
    }

    /**
     * This is run if the user clicks the form's cancel button.
     * A confirmation modal might pop up if there are any changes.
     */
    @action.bound private showConfirmAndCloseForm(): void {
        // If there were any changes, i.e. if the original and mutable copy aren't the same,
        // open the confirmation modal for aborting the update process.
        if (this.addedVehicleGroups.length > 0 || this.addedUsers.length > 0) {
            this.cancelConfirmationModalVisible = true;
            return;
        }

        this.closeForm();
    }

    @action.bound private toggleVehicleGroupListVisible(): void {
        this.vehicleGroupListVisible = !this.vehicleGroupListVisible;
    }

    /**
     * Actually close the component and reset all state.
     */
    @action.bound private closeForm(): void {
        this.reset();
        this.cancelConfirmationModalVisible = false;
        this.props.onDialogCancel();
    }

    @action.bound private closeCancelConfirmationModalVisible(): void {
        this.cancelConfirmationModalVisible = false;
    }

    /**
     * Handle the actual addition of all currently selected Users in the group.
     */
    @action.bound private addUsers(users: UUID[]): void {
        this.addedUsers = [...this.addedUsers, ...users];
    }

    /**
     * Handle the removal of Users.
     * In this case, removal means that they'll be removed from the list of added Users.
     */
    @action.bound private removeUsers(idsToRemove: UUID[]): void {
        const remainingUsers = this.addedUsers.filter((id) => !idsToRemove.includes(id));
        this.addedUsers = remainingUsers;
    }

    /**
     * Handle the actual addition of all currently selected Vehicles in the group.
     */
    @action.bound private addVehicleGroups(vehicleGroups: UUID[]): void {
        this.addedVehicleGroups = [...this.addedVehicleGroups, ...vehicleGroups];
    }

    /**
     * Handle the removal of VehicleGroups.
     * In this case, removal means that they'll be removed from the list of added VehicleGroups.
     */
    @action.bound private removeVehicleGroups(idsToRemove: UUID[]): void {
        const remainingGroups = this.addedVehicleGroups.filter((id) => !idsToRemove.includes(id));
        this.addedVehicleGroups = remainingGroups;
    }

    /**
     * The items that should be shown in the list of added VehicleGroups.
     */
    @computed private get vehicleGroupItems(): object[] {
        const groups = [];

        for (const id of this.addedVehicleGroups) {
            const group = store.repositoryVehicleGroups.byId(id);
            if (group !== undefined) {
                groups.push({
                    key: group.id,
                    label: group.label,
                    assignedVehicleCount: group.assignedVehicleCount,
                    assignedUserGroupCount: group.assignedUserGroupCount,
                });
            }
        }

        return groups;
    }

    /**
     * The columns that should be shown in the list of selected VehicleGroups.
     */
    @computed private get vehicleGroupColumns(): IColumn[] {
        return [
            {
                fieldName: "label",
                name: store.i18n.t("listVehicleGroups.column.label.name"),
                key: "label",
                minWidth: 100,
            },
            {
                fieldName: "assignedVehicleCount",
                name: store.i18n.t("listVehicleGroups.column.assignedVehicleCount.name"),
                key: "assignedVehicleCount",
                minWidth: 80,
            },
            {
                fieldName: "assignedUserGroupCount",
                name: store.i18n.t("listVehicleGroups.column.assignedUserGroupCount.name"),
                key: "assignedUserGroupCount",
                minWidth: 150,
            },
        ];
    }

    /**
     * The items that should be shown in the list of added Users.
     */
    @computed private get userItems(): object[] {
        const users = [];

        for (const id of this.addedUsers) {
            const user = store.repositoryUsers.byId(id);
            if (user !== undefined) {
                users.push({
                    key: user.id,
                    employeeId: user.employeeId,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    jobTitle: user.jobTitle,
                    fleetRole: store.i18n.formatFleetRole(user.fleetRole),
                });
            }
        }

        // Sort the users by last name.
        users.sort((a, b) => a.lastName.localeCompare(b.lastName));
        return users;
    }

    /**
     * The columns that should be shown in the list of selected Users.
     */
    @computed private get userColumns(): IColumn[] {
        return [
            {
                fieldName: "employeeId",
                name: store.i18n.t("listUsers.column.employeeId.name"),
                key: "employeeId",
                minWidth: 200,
                maxWidth: 300,
            },
            {
                fieldName: "firstName",
                name: store.i18n.t("listUsers.column.firstName.name"),
                key: "firstName",
                minWidth: 80,
                maxWidth: 100,
            },
            {
                fieldName: "lastName",
                name: store.i18n.t("listUsers.column.lastName.name"),
                key: "lastName",
                minWidth: 100,
                maxWidth: 150,
            },
            {
                fieldName: "jobtitle",
                name: store.i18n.t("listUsers.column.jobTitle.name"),
                key: "jobTitle",
                minWidth: 100,
                maxWidth: 150,
            },
            {
                fieldName: "fleetRole",
                name: store.i18n.t("listUsers.column.fleetRole.name"),
                key: "fleetRole",
                minWidth: 120,
                maxWidth: 200,
            },
        ];
    }

    public render(): JSX.Element {
        return (
            <ElofleetDialog
                isOpen={this.props.isOpen}
                title={store.i18n.t("formCreateUserGroup.title")}
                onDismiss={this.showConfirmAndCloseForm}
            >
                <form onSubmit={this.createUserGroup}>
                    <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                        <TextField
                            label={store.i18n.t("formCreateUserGroup.label.label")}
                            {...doubleBindString<FormCreateUserGroup>(this, "label", () =>
                                this.triggerValidation(),
                            )}
                            required
                            errorMessage={store.i18n.formatFieldValidationState(
                                store.repositoryUserGroups.validation.create.getFieldValidationState(
                                    this.validationId,
                                    "label",
                                ),
                            )}
                        />
                    </Stack>
                    <Stack
                        horizontal
                        styles={{
                            root: {
                                minWidth: "1200px",
                                minHeight: "400px",
                                padding: sizes.xl,
                            },
                        }}
                    >
                        <Stack.Item tokens={{ padding: `0px 0px 0px ${sizes.m}` }}>
                            <MultiSelectWithList
                                repository={store.repositoryUsers}
                                query={{
                                    pageSize: defaultPageSize,
                                    sortKey: UserSortKey.LAST_NAME,
                                    sortDirection: SortDirection.ASCENDING,
                                }}
                                onAdd={this.addUsers}
                                onDelete={this.removeUsers}
                                listStateId={this.userListStateId}
                                listState={store.serviceListStates.users}
                                addedEntities={this.addedUsers}
                                columns={this.userColumns}
                                items={this.userItems}
                                formatEntity={(user: User) => store.i18n.formatUserFullName(user)}
                                addButtonText={store.i18n.t("component.multiSelect.addButton")}
                                removeButtonText={store.i18n.t(
                                    "component.multiSelect.removeButton",
                                )}
                                dropdownLabel={store.i18n.t("formCreateUserGroup.user.label")}
                                multiSelectPlaceholder={store.i18n.t(
                                    "formCreateUserGroup.user.placeholder",
                                )}
                            />
                        </Stack.Item>
                        {this.vehicleGroupListVisible && (
                            <Stack.Item tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                                <Separator vertical />
                            </Stack.Item>
                        )}
                        {this.vehicleGroupListVisible && (
                            <Stack.Item tokens={{ padding: `0px ${sizes.m} 0px 0px` }}>
                                <MultiSelectWithList
                                    repository={store.repositoryVehicleGroups}
                                    query={{
                                        pageSize: defaultPageSize,
                                    }}
                                    onAdd={this.addVehicleGroups}
                                    onDelete={this.removeVehicleGroups}
                                    listStateId={this.vehicleGroupListStateId}
                                    listState={store.serviceListStates.vehicleGroups}
                                    addedEntities={this.addedVehicleGroups}
                                    columns={this.vehicleGroupColumns}
                                    items={this.vehicleGroupItems}
                                    formatEntity={(group: VehicleGroup) => group.label}
                                    addButtonText={store.i18n.t("component.multiSelect.addButton")}
                                    removeButtonText={store.i18n.t(
                                        "component.multiSelect.removeButton",
                                    )}
                                    dropdownLabel={store.i18n.t(
                                        "formCreateUserGroup.vehicleGroup.label",
                                    )}
                                    multiSelectPlaceholder={store.i18n.t(
                                        "formCreateUserGroup.vehicleGroup.placeholder",
                                    )}
                                />
                            </Stack.Item>
                        )}
                    </Stack>
                    {this.props.asDialogContent ? (
                        <ElofleetDialogFooter style={{ justifyContent: "space-between" }}>
                            <DefaultButton
                                text={store.i18n.t(
                                    "formCreateUserGroup.toggleVehicleGroupList.text",
                                )}
                                onClick={this.toggleVehicleGroupListVisible}
                            />
                            <Stack.Item>
                                <Stack
                                    horizontal
                                    tokens={{
                                        childrenGap: "1em",
                                    }}
                                >
                                    <DefaultButton
                                        text={store.i18n.t("formCreateUserGroup.cancel.text")}
                                        onClick={this.showConfirmAndCloseForm}
                                    />
                                    <PrimaryButtonValidation
                                        text={store.i18n.t("formCreateUserGroup.submit.text")}
                                        validationId={this.validationId}
                                        validation={store.repositoryUserGroups.validation.create}
                                    />
                                </Stack>
                            </Stack.Item>
                        </ElofleetDialogFooter>
                    ) : (
                        <Stack horizontal horizontalAlign="end">
                            <PrimaryButtonValidation
                                text={store.i18n.t("formCreateUserGroup.submit.text")}
                                validationId={this.validationId}
                                validation={store.repositoryUserGroups.validation.create}
                            />
                        </Stack>
                    )}
                    <ModalConfirmation
                        isOpen={this.cancelConfirmationModalVisible}
                        title={store.i18n.t("modalAbortUpdate.title")}
                        text={store.i18n.t("modalAbortUpdate.description")}
                        onConfirm={this.closeForm}
                        onCancel={this.closeCancelConfirmationModalVisible}
                    />
                </form>
            </ElofleetDialog>
        );
    }
}
